/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import EditStoreDialog from "components/Dialogs/EditStoreDialog";

import { useSnackbar } from "providers/SnackbarProvider";

function Author({ name, username, email }) {
  return (
    <SoftBox display="flex" alignItems="center" px={1} py={0.5}>
      <SoftBox mr={2}>
        <SoftAvatar
          src={`https://ui-avatars.com/api/?name=${name}`}
          alt={name}
          size="sm"
          variant="rounded"
        />
      </SoftBox>
      <SoftBox display="flex" flexDirection="column">
        <SoftTypography variant="button" fontWeight="medium">
          {name} ({username})
        </SoftTypography>
        <SoftTypography variant="caption" color="secondary">
          {email}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function Function({ job, org }) {
  return (
    <SoftBox display="flex" flexDirection="column">
      <SoftTypography variant="caption" fontWeight="medium" color="text">
        {job}
      </SoftTypography>
      <SoftTypography variant="caption" color="secondary">
        {org}
      </SoftTypography>
    </SoftBox>
  );
}

const apiUrl = "https://api.getverceipt.com/api/v1";

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function useAuthorsTableData() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedStore, setSelectedStore] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [storeAddrError, setStoreAddrError] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleEditClick = (store) => {
    setSelectedStore(store);
    setDialogOpen(true);
  };

  const handleSave = async (updatedStore) => {
    try {
      if (updatedStore.address.length < 3) {
        throw new Error("Store names must be at least 3 characters in length.");
      }

      await fetch(`${apiUrl}/business/store/update/${updatedStore.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ address: updatedStore.address }),
      });
      await fetchData();

      setDialogOpen(false);
      showSnackbar("Successfully updated store information!", "success");
    } catch (err) {
        showSnackbar(`${err}`, "error");
        setStoreAddrError(true);    
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm("Are you sure you want to delete this store?")) return;
    try {
      await fetch(`${apiUrl}/business/store/delete/${storeId}`, {
        method: "DELETE",
        credentials: "include",
      });
      await fetchData();
      showSnackbar("Successfully deleted the store.", "success");
    } catch (err) {
      console.error("Error deleting store:", err);
    } finally {
      setDialogOpen(false);
    }
  };

  const editDialog = (
    <EditStoreDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      store={selectedStore}
      onSave={handleSave}
      onDelete={handleDelete}
      error={storeAddrError}
    />
  );

  const columns = [
    { name: "id", align: "center" },
    { name: "address", align: "left" },
    { name: "created", align: "center" },
    { name: "action", align: "center" }
  ];

  const [maxStores, setMaxStores] = useState(0);
  const [storeCount, setStoreCount] = useState(0);
  useEffect(() => {
    fetchData();
  }, []);

  // ✅ move fetchData outside the effect
  const fetchData = async () => {
    try {
      const response = await fetch(`${apiUrl}/business/stores/all`, {
        method: 'GET',
        credentials: 'include', // this alone is enough; withCredentials isn't valid for fetch
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      console.log(data);

      setMaxStores(data.maxStores);
      setStoreCount(data.stores.length);

      const formattedRows = data.stores.map((user) => ({
        id: (
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {user.id}
          </SoftTypography>
        ),
        address: (
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {user.address}
          </SoftTypography>
        ),
        created: (
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {formatDate(user.created_at)}
          </SoftTypography>
        ),
        action: (
          <SoftTypography
            component="a"
            href="#"
            variant="caption"
            color="secondary"
            fontWeight="medium"
            onClick={() => handleEditClick(user)}
          >
            Edit
          </SoftTypography>
        ),
      }));

      setRows(formattedRows);
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    } finally {
      setLoading(false);
    }
  };

  return { columns, rows, loading, storeCount, maxStores, refetch: fetchData, editDialog };
}
